import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import WarningIcon from '@mui/icons-material/Warning';
import { uniqBy } from 'lodash';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Product } from '../../interfaces/Product/Product';
import { getProduct } from '../../api/Product';
import { bodyWidth, sharedColors } from '../../utils/Style';
import { ProductGallery } from '../../components/ProductGallery/ProductGallery';
import { ProductOptionValue } from '../../interfaces/Product/ProductOption';
import { formatCurrency } from '../../utils/Currency';
import { useThemeColor } from '../../hooks/useThemeColor';
import { calculateDiscountRate } from '../../utils/Pricing';
import { ProductReview } from '../../interfaces/Product/ProductReview';
import { getProductReviews } from '../../api/Review';
import { ProductReviewSummary } from './ProductReviewSummary';
import { ProductNotes } from './ProductNotes';
import { OptionDropdown } from '../../components/Dropdowns/OptionDropdown';
import { QuantityDropdown } from '../../components/Dropdowns/QuantityDropdown';
import { ProductsCarousel } from '../../components/ProductsCarousel/ProductsCarousel';
import { ProductReviewRow } from './ProductReviewRow';
import { isTokenValid } from '../../utils/Auth';
import { addProductToWishlist } from '../../api/Wishlist';
import { Waiting } from '../../components/Waiting/Waiting';
import { addItemToBasket } from '../../utils/Basket';
import { setStockAlert } from '../../api/StockAlert';
import { useBasket } from '../../hooks/useBasket';
import { AddReviewCard } from './AddReviewCard';
import { ProductHelmet } from './ProductHelmet';
import { ProductBreadcrumbs } from '../../components/Breadcrumbs/ProductBreadcrumbs';
import { PopupSlot } from '../../components/Popup/PopupSlot';

export const ProductDetailsPage = () => {
  const navigate = useNavigate();

  const { primary, secondary, ternary } = useThemeColor();

  const { slug } = useParams<string>();

  const { refreshBasket } = useBasket();

  const [product, setProduct] = useState<Product | undefined>(undefined);
  const [review, setReview] = useState<ProductReview | undefined>(undefined);
  const [selectedOption, setSelectedOption] = useState<
    ProductOptionValue | undefined
  >(undefined);
  const [selectedAmount, setSelectedAmount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addingToBasket, setAddingToBasket] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSelectedOption(undefined);

    getProduct(slug ?? '')
      .then(setProduct)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (product?.id) {
      getProductReviews({
        productID: product.id,
        page: 0,
        pageSize: 100,
      })
        .then(setReview)
        .catch((err) => toast.error(err.message));
    }

    setSelectedOption(product?.options[0]?.values[0]);
  }, [product]);

  useEffect(() => {
    setSelectedAmount(1);
  }, [product, selectedOption]);

  const handleAddReview = () => {
    if (!isTokenValid()) {
      navigate('/giris-yap');
      return;
    }

    setShowReviewForm(true);
  };

  const handleAddToWishlist = () => {
    if (!isTokenValid()) {
      navigate('/giris-yap');
      return;
    }

    if (!product) {
      return;
    }

    addProductToWishlist(product.id)
      .then(() => toast.success('Ürün favorilere eklendi.'))
      .catch((err) => toast.error(err.message));
  };

  const handleAddToBasket = () => {
    if (!product) {
      return;
    }

    setAddingToBasket(true);
    addItemToBasket({
      productID: product.id,
      quantity: selectedAmount,
      optionValueID: selectedOption?.id,
    })
      .then(() => {
        refreshBasket();
        toast.success('Ürün sepete eklendi.');
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setAddingToBasket(false));
  };

  const handleSetStockAlert = () => {
    if (!product) {
      return;
    }

    if (isTokenValid()) {
      setStockAlert({
        productID: product.id,
        optionValueID: selectedOption?.id,
      })
        .then(() => {
          toast.success(
            'Ürün stokları yenilendiğinde E-Posta ile haberdar edileceksiniz',
          );
        })
        .catch((error) => toast.error(error.message));
    } else {
      navigate('/giris-yap');
    }
  };

  const specialPrice = selectedOption
    ? selectedOption.specialPrice
    : product?.specialPrice?.amount;
  const price = selectedOption
    ? selectedOption.price.amount
    : product?.price?.amount;
  const resolvedPrice = specialPrice || price;
  const discountRate = calculateDiscountRate(price ?? 0, specialPrice ?? 0);

  const quantity = selectedOption?.stock ?? product?.qty;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      {!!product && <ProductHelmet product={product} review={review} />}

      <PopupSlot productSlug={slug} />

      <Waiting open={addingToBasket} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 3.5,
          boxSizing: 'border-box',
        }}
      >
        {loading && (
          <CircularProgress color='primary' sx={{ alignSelf: 'center' }} />
        )}
        {!loading && !product && (
          <Typography>
            Ürün bulunamadı. Lütfen daha sonra tekrar deneyiniz.
          </Typography>
        )}

        {!!product && <ProductBreadcrumbs product={product} />}

        {!!product && (
          <Grid container spacing={3} width='100%' columns={20}>
            <Grid
              size={{
                xs: 20,
                lg: 10,
                xl: 8,
              }}
            >
              <ProductGallery
                images={uniqBy(
                  [
                    ...(selectedOption?.image
                      ? [{ path: selectedOption.image }]
                      : []),
                    product.baseImage,
                    ...product.files,
                  ],
                  (x) => x.path,
                )}
                videoURL={product.videoUrl}
              />
            </Grid>

            <Grid size={{ xs: 20, lg: 10, xl: 12 }}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: sharedColors.gray7,
                    fontWeight: 600,
                    fontSize: '1.375rem',
                  }}
                >
                  {product.name}
                </Typography>

                <ProductReviewSummary product={product} reviews={review} />

                {!!specialPrice && (
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      textDecoration: 'line-through',
                    }}
                  >
                    {price ? formatCurrency(price) : product.price.formatted}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1.5,
                    alignItems: 'stretch',
                  }}
                >
                  <Typography
                    sx={{
                      color: primary,
                      fontSize: '28px',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {resolvedPrice
                      ? formatCurrency(resolvedPrice)
                      : product.specialPrice?.formatted ??
                        product.price.formatted}
                  </Typography>

                  {!!discountRate && (
                    <Box
                      sx={{
                        backgroundColor: primary,
                        px: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        borderRadius: '3px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: secondary,
                          fontWeight: 600,
                          fontSize: '13px',
                        }}
                      >{`%${discountRate} tasarruf`}</Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: sharedColors.gray7,
                  }}
                >
                  {product.shortDescription}
                </Typography>

                <ProductNotes product={product} option={selectedOption} />

                {product.options.map((option) => (
                  <OptionDropdown
                    key={option.id}
                    option={option}
                    value={selectedOption}
                    onChange={setSelectedOption}
                  />
                ))}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1.5,
                    alignItems: 'center',
                    mt: 1.5,
                  }}
                >
                  {!!quantity && (
                    <QuantityDropdown
                      availableQuantity={quantity ?? 0}
                      value={selectedAmount}
                      onChange={setSelectedAmount}
                    />
                  )}

                  <Button
                    onClick={quantity ? handleAddToBasket : handleSetStockAlert}
                    sx={{
                      fontWeight: 700,
                      fontSize: '13px',
                      px: '15px',
                      height: '40px',
                      color: secondary,
                      backgroundColor: quantity ? primary : ternary,
                      transition: 'background-color 0.3s ease',
                      borderRadius: 0,
                      '&:hover': {
                        backgroundColor: ternary,
                      },
                    }}
                  >
                    {quantity ? 'Sepete Ekle' : 'Gelince Haber Ver'}
                  </Button>

                  <Tooltip title='Favorilere Ekle'>
                    <Button
                      onClick={handleAddToWishlist}
                      variant='contained'
                      sx={{
                        px: 1.5,
                        height: '40px',
                        color: secondary,
                        backgroundColor: primary,
                        transition: 'background-color 0.3s ease',
                        '&:hover': {
                          backgroundColor: ternary,
                        },
                        minWidth: 0,
                      }}
                    >
                      <FavoriteIcon />
                    </Button>
                  </Tooltip>

                  {!!quantity && quantity < 6 && (
                    <>
                      <WarningIcon sx={{ color: sharedColors.statusOrange }} />
                      <Typography
                        sx={{
                          ml: -1,
                          fontSize: '15px',
                          fontWeight: 700,
                          color: sharedColors.gray7,
                        }}
                      >
                        Tükenmek Üzere
                      </Typography>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  {product.categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/kategori/${category.slug}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <Chip
                        key={category.id}
                        label={category.name}
                        sx={{
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: primary,
                            color: secondary,
                          },
                        }}
                      />
                    </Link>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {!!product && (
          <>
            <ProductsCarousel
              title='Benzer Ürünler'
              relatedProductID={product.id}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                border: '2px solid #e1e1e1',
              }}
            >
              <Box
                sx={{
                  p: '10px 13px',
                  borderRight: '2px solid #e1e1e1',
                  width: 'fit-content',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: sharedColors.gray7,
                  }}
                >
                  Açıklama
                </Typography>
              </Box>

              <Box
                sx={{
                  width: '100%',
                  p: '20px',
                  boxSizing: 'border-box',
                  borderTop: '2px solid #e1e1e1',
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{
                    __html: product?.description ?? '',
                  }}
                  sx={{ '& *': { wordBreak: 'break-word' } }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1.5,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: sharedColors.gray7,
                  }}
                >
                  Değerlendirmeler
                </Typography>

                <Button
                  startIcon={<EditNoteIcon />}
                  size='small'
                  variant='outlined'
                  onClick={handleAddReview}
                  sx={{
                    borderColor: primary,
                    color: primary,
                    textTransform: 'none',
                  }}
                >
                  Değerlendirme Yaz
                </Button>
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {review?.reviews.map((r, i) => (
                  <ProductReviewRow key={i} review={r} />
                ))}
              </Box>

              {!review?.totalCount && !showReviewForm && (
                <Typography
                  sx={{ alignSelf: 'center', color: sharedColors.gray5 }}
                >
                  Bu ürün için henüz bir değerlendirme yazılmamış
                </Typography>
              )}

              {showReviewForm && !!product && (
                <AddReviewCard
                  productID={product.id}
                  onClose={() => setShowReviewForm(false)}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
